import React from 'react';
import {
  TextField, Typography, Box, Grid, Button, Alert, Snackbar, Paper,
} from '@mui/material';
import { useEncoderForm } from '../hooks/useEncoderForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: FORM_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: FORM_COLORS.primary, borderWidth: 2 },
  },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const EncoderView = ({ idPaciente, tiempo5STS = '' }) => {
  const {
    formData,
    openGood,
    openError,
    setOpenGood,
    setOpenError,
    handleChange,
    guardarDatos,
  } = useEncoderForm({ idPaciente, tiempo5STS });

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Paper
        elevation={0}
        sx={{ width: '100%', p: { xs: 2, md: 3 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
          5STS / Encoder
        </Typography>
        <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
          Registro de velocidad, potencia y rendimiento funcional.
        </Typography>

        <TextField
          label="ID del paciente"
          variant="outlined"
          value={idPaciente}
          disabled
          sx={{ mb: 4, ...fieldSx }}
        />

        <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 3 }}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
            Prueba 5 veces sentado-levantado (5STS)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código de Prueba Encoder"
                variant="outlined"
                name="codigoPrueba"
                value={formData.codigoPrueba}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tiempo 5STS (s)"
                variant="outlined"
                name="tiempo5STS"
                value={formData.tiempo5STS}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 3 }}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
            Datos del Encoder
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Velocidad (m/s)"
                variant="outlined"
                name="velocidad"
                value={formData.velocidad}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Potencia (W)"
                variant="outlined"
                name="potencia"
                value={formData.potencia}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Distancia (cm)"
                variant="outlined"
                name="distancia"
                value={formData.distancia}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
            Resumen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ color: FORM_COLORS.textMuted }}>
                <strong>5STS:</strong> {formData.tiempo5STS || 'N/A'} s
              </Typography>
              <Typography sx={{ color: FORM_COLORS.textMuted }}>
                <strong>Código Encoder:</strong> {formData.codigoPrueba || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ color: FORM_COLORS.textMuted }}>
                <strong>Velocidad:</strong> {formData.velocidad ? `${formData.velocidad} m/s` : 'N/A'}
              </Typography>
              <Typography sx={{ color: FORM_COLORS.textMuted }}>
                <strong>Potencia:</strong> {formData.potencia ? `${formData.potencia} W` : 'N/A'}
              </Typography>
              <Typography sx={{ color: FORM_COLORS.textMuted }}>
                <strong>Distancia:</strong> {formData.distancia ? `${formData.distancia} cm` : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
            Guardar datos
          </Button>
        </Box>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>
            ¡Encoder creado correctamente!
          </Alert>
        </Snackbar>
        <Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
          <Alert severity="error" onClose={() => setOpenError(false)}>
            Error al guardar el encoder.
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default EncoderView;
